import React, { useState } from "react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import Email, { EmailText } from "@/components/Email";
import { api } from "@/lib/api";
import { CONTACT } from "@/constants/testIds";

const PERSONAS = [
  { v: "", label: "— Select —" },
  { v: "individual", label: "Individual" },
  { v: "startup", label: "Startup" },
  { v: "corporate", label: "Corporate" },
  { v: "investor", label: "Investor" },
  { v: "media", label: "Media / Press" },
  { v: "other", label: "Other" },
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "", email: "", organization: "", persona: "", subject: "", message: "",
  });
  const [status, setStatus] = useState({ state: "idle", text: "" });

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "sending", text: "Transmitting…" });
    try {
      const payload = { ...form };
      if (!payload.persona) delete payload.persona;
      if (!payload.organization) delete payload.organization;
      const res = await api.contact(payload);
      const delivered = res?.delivery?.delivered;
      setStatus({
        state: "ok",
        text: delivered
          ? "Received. Our team at " + "support" + "\u0040" + "forte2.space" + " will reply within 2 business days."
          : "Received & logged. Email relay is offline; our team will still respond from " + "support" + "\u0040" + "forte2.space" + ".",
      });
      setForm({ name: "", email: "", organization: "", persona: "", subject: "", message: "" });
    } catch (err) {
      setStatus({ state: "err", text: "Could not send. Please email " + "support" + "\u0040" + "forte2.space" + " directly." });
    }
  };

  return (
    <div className="bg-[color:var(--f2-bg)] min-h-screen text-white flex flex-col">
      <Nav />
      <div className="flex-1">
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 grid-lines opacity-40 pointer-events-none" />
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="label-xs mb-2">§ Contact</div>
              <h1 className="font-display uppercase tracking-tighter font-black text-4xl md:text-6xl">
                Talk to <span className="saffron">Forte² Space.</span>
              </h1>
              <p className="mt-6 text-[color:var(--f2-muted)] max-w-md leading-relaxed">
                For partnerships, cohort onboarding, mentor applications, corporate assessments and press —
                write to us. All messages route to <EmailText className="saffron" />.
              </p>
              <div className="mt-8 hard-border p-6 bg-[color:var(--f2-surface)]">
                <div className="label-xs mb-2">Registered Entity</div>
                <div className="text-sm">Gritly Analytics Private Limited</div>
                <div className="text-xs text-[color:var(--f2-muted)] mt-1">Chennai, Tamil Nadu, India</div>
                <div className="label-xs mt-6 mb-2">Support Inbox</div>
                <Email
                  data-testid="contact-support-inbox"
                  className="saffron text-base hover:underline"
                />
                <div className="label-xs mt-6 mb-2">Response Time</div>
                <div className="text-sm">Within 2 business days (Mon–Fri, IST)</div>
              </div>
            </div>

            <form
              onSubmit={submit}
              className="lg:col-span-7 hard-border p-8 bg-[color:var(--f2-surface)] space-y-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label-xs block mb-2">Full Name</label>
                  <input
                    data-testid={CONTACT.name}
                    required
                    className="input-crisp"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="label-xs block mb-2">Email</label>
                  <input
                    type="email"
                    required
                    data-testid={CONTACT.email}
                    className="input-crisp"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label-xs block mb-2">Organization</label>
                  <input
                    data-testid={CONTACT.org}
                    className="input-crisp"
                    value={form.organization}
                    onChange={(e) => setForm({ ...form, organization: e.target.value })}
                  />
                </div>
                <div>
                  <label className="label-xs block mb-2">I am a</label>
                  <select
                    data-testid={CONTACT.persona}
                    className="input-crisp"
                    value={form.persona}
                    onChange={(e) => setForm({ ...form, persona: e.target.value })}
                  >
                    {PERSONAS.map((p) => (
                      <option key={p.v} value={p.v}>{p.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="label-xs block mb-2">Subject</label>
                <input
                  data-testid={CONTACT.subject}
                  required
                  className="input-crisp"
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                />
              </div>
              <div>
                <label className="label-xs block mb-2">Message</label>
                <textarea
                  data-testid={CONTACT.message}
                  required
                  className="input-crisp min-h-[160px]"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                />
              </div>
              <button
                data-testid={CONTACT.submit}
                type="submit"
                disabled={status.state === "sending"}
                className="btn-crisp btn-primary"
              >
                {status.state === "sending" ? "Transmitting…" : "Send Message →"}
              </button>
              {status.state === "ok" && (
                <div data-testid={CONTACT.success} className="saffron text-sm">{status.text}</div>
              )}
              {status.state === "err" && (
                <div data-testid={CONTACT.error} className="text-red-400 text-sm">{status.text}</div>
              )}
              <p className="text-xs text-[color:var(--f2-muted)] leading-relaxed">
                By submitting, you consent to processing of the above data under India&apos;s Digital Personal Data
                Protection Act, 2023, for the sole purpose of responding to your query. You may withdraw consent
                any time by writing to <EmailText />.
              </p>
            </form>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
